import {
  type StrongOrEmphasisMarkerToken,
  type Token,
  TokenType,
} from './tokens'
import {
  type MinidownNode,
  MinidownNodeType,
  type Root,
  type Text as TextNode,
} from './nodes'
import { tokenEquals, tokenize } from './tokenize'
import { splitByUrls } from '@/minidown/links'

/**
 * Parses Minidown to an AST.
 */
export function parse(source: string): Root {
  const tokens = tokenize(source)

  let pos = 0
  const eos = tokens.length
  // Parse the root.
  return parseRoot()

  /**
   * Starts parsing at the root.
   */
  function parseRoot() {
    const root: Root = {
      type: MinidownNodeType.Root,
      children: [],
    }
    while (pos < eos) {
      const node = parseBlock()
      appendNodes(root.children, node)
    }

    return root
  }

  /**
   * Attempts to parse a block node, such as a paragraph.
   */
  function parseBlock(): MinidownNode | MinidownNode[] {
    // Start by trimming any leading spacing
    while (pos < eos) {
      const token = tokens[pos]
      if (token.type !== TokenType.Space && token.type !== TokenType.Newline) {
        // Not whitespace/newline, so we're done looking.
        break
      }
      pos++
    }

    return parseParagraph()
  }

  /**
   * Parses a paragraph.
   */
  function parseParagraph(): MinidownNode {
    const nodes: MinidownNode[] = []
    while (pos < eos) {
      const node = parseInline(true)
      appendNodes(nodes, node)

      // Look ahead and check if there are 2 or more consecutive newlines,
      // ignoring spaces.
      let specPos = pos
      let newlineCount = 0
      while (specPos < eos) {
        const token = tokens[specPos]
        if (token.type === TokenType.Newline) {
          specPos++
          newlineCount++
          continue
        }

        if (token.type === TokenType.Space) {
          specPos++
          continue
        }

        break
      }

      // If we got at least 2 newlines, or we reached the end while looking
      // for spacing, then we'll consume everything we looked at.
      if (newlineCount >= 2 || specPos >= eos) {
        pos = specPos
        break
      }

      // If we got a single newline, we'll emit it as a Break node.
      if (newlineCount === 1) {
        pos = specPos
        appendNodes(nodes, {
          type: MinidownNodeType.Break,
          value: tokens[pos - 1].value,
        })
      }
    }

    return {
      type: MinidownNodeType.Paragraph,
      children: nodes,
    }
  }

  /**
   * Parses inline content nodes.
   */
  function parseInline(linkify: boolean): MinidownNode | MinidownNode[] {
    const token = tokens[pos]

    if (token.type === TokenType.StrongOrEmphasisMarker) {
      return parseFormatting(token)
    }

    if (token.type === TokenType.OpenBracket) {
      return parseLink()
    }

    if (token.type === TokenType.Text) {
      return parseText(linkify)
    }

    // Consume whatever it is as return as text
    pos++
    return {
      type: MinidownNodeType.Text,
      value: token.value,
    }
  }

  /**
   * Parses formatting tokens like **strong** and _emphasis_.
   * @param token
   */
  function parseFormatting(token: StrongOrEmphasisMarkerToken) {
    pos++
    // Check if the next token is the same type of marker with the same value.
    // If it is, then it could be the start of a strong node.
    // If not, then it could be the start of an emphasis node.
    if (pos < eos && tokenEquals(token, tokens[pos])) {
      pos++
      // The tokens match, so it could be a `strong` node
      return parseStrong(token)
    }

    // Token don't match, attempt parsing an `emphasis` node
    return parseEmphasis(token)
  }

  /**
   * Parses a link.
   */
  function parseLink(): MinidownNode | MinidownNode[] {
    const nodes: MinidownNode[] = []

    // Consume the open bracket and parse the link content
    pos++

    // Record the position at which we started parsing the link.
    // We may need to restore it if we bail.
    const posAtBracketStart = pos

    // Parse the content of the link
    while (pos < eos) {
      const token = tokens[pos]
      if (token.type === TokenType.CloseBracket) {
        // We've reached the end of the link content - consume the close bracket
        pos++
        break
      }

      // If we're on a newline, then we're not parsing a link
      if (token.type === TokenType.Newline) {
        // Add back the bracket as text.
        return prependText(nodes, '[')
      }

      const node = parseInline(false)
      appendNodes(nodes, node)
    }

    // Track whether we should abort parsing the link.
    let abort = false

    if (pos < eos) {
      // Start parsing the URL.
      const token = tokens[pos]

      if (token.type !== TokenType.OpenParen) {
        // Not a link
        abort = true
      } else {
        // If any of the parsed nodes are links, then we'll treat the link we were
        // parsing as text.
        for (const node of nodes) {
          if (node.type === MinidownNodeType.Link) {
            abort = true
            break
          }
        }
      }
    } else {
      abort = true
    }

    // If we're not parsing a link, clear the nodes we've collected so far,
    // add the bracket as text, then try parsing inline again.
    if (abort) {
      // Clear the nodes collected
      nodes.length = 0

      // Add the bracket as text.
      prependText(nodes, '[')

      // Reset the position to immediately after the bracket.
      pos = posAtBracketStart

      // Make sure there's still content left
      if (pos >= eos) {
        return nodes
      }

      // Parse an inline node, with linkify enabled.
      appendNodes(nodes, parseInline(true))
      return nodes
    }

    // Consume the open paren
    pos++

    let url = ''
    let closed = false
    while (pos < eos) {
      const token = tokens[pos++]
      if (token.type === TokenType.CloseParen) {
        // We've found the closing paren for the URL
        closed = true
        break
      }

      if (token.type === TokenType.Newline) {
        // Newlines are definitely not valid in URLs
        break
      }

      url += token.value
    }

    // Trim the URL and check if it contains any spaces - if it does, then
    // we won't consider it a valid link.
    const trimmed = url.trim()

    if (!closed || trimmed.match(/\s/g)) {
      // We didn't find the closing paren for the URL, or the URL was invalid,
      // so we'll add the tokens we consumed as text.
      prependText(nodes, `[`)
      appendText(nodes, closed ? `](${url})` : `](${url}`)
      return nodes
    }

    return {
      type: MinidownNodeType.Link,
      url: trimmed,
      children: nodes,
    }
  }

  /**
   * Parses a strong node.
   * @param startToken
   */
  function parseStrong(startToken: Token): MinidownNode | MinidownNode[] {
    const repeatedStartToken = startToken.value.repeat(2)
    if (pos < eos) {
      // Content of strong tags cannot start with a space.
      const token = tokens[pos]
      if (token.type === TokenType.Space) {
        // Don't advance the token stream, just return the strong marker as text
        return {
          type: MinidownNodeType.Text,
          value: repeatedStartToken,
        }
      }
    }

    const children: MinidownNode[] = []
    let previousWasSpace = false
    while (pos < eos) {
      const token = tokens[pos]

      // Check for the first part of the terminating token sequence
      if (tokenEquals(startToken, token)) {
        pos++

        // The next token must also match the start token, otherwise it's
        // not terminating the strong node.
        if (pos < eos && tokenEquals(startToken, tokens[pos])) {
          pos++
          if (children.length > 0) {
            if (previousWasSpace) {
              // Whitespace before the closing tag is not allowed, so turn this
              // into a text node instead.
              prependText(children, repeatedStartToken)
              appendText(children, repeatedStartToken)
              return children
            }

            // Finish the strong node
            return {
              type: MinidownNodeType.Strong,
              // We're repeating the value character twice since
              // it's always 2 consecutive `_` or `*`s to indicate a strong
              // node.
              value: repeatedStartToken,
              children,
            }
          } else {
            // It was a completely empty strong node, so we'll return the
            // markers as text.
            return {
              type: MinidownNodeType.Text,
              // The value of `repeatedStartToken` will be either `__` or `**`
              // depending on the start token, so we'll repeat it to
              // return the same number of characters.
              value: repeatedStartToken.repeat(2),
            }
          }
        }

        // We didn't find the final terminating token (`__missing one_`).
        // We'll add the tokens we consumed as a text node.
        appendText(children, startToken.value)
        continue
      }

      previousWasSpace = token.type === TokenType.Space

      // Still parsing content inside the node
      appendNodes(children, parseInline(true))
    }

    // We didn't find a single terminating token, so we'll return
    // the token itself as a text node as well as the parsed children
    return prependText(children, repeatedStartToken)
  }

  /**
   * Parses an emphasis node.
   *
   * @param startToken
   */
  function parseEmphasis(startToken: Token): MinidownNode | MinidownNode[] {
    if (pos < eos) {
      // Content of emphasis tags cannot start with a space.
      const token = tokens[pos]
      if (token.type === TokenType.Space) {
        // Don't advance the token stream, just return the emphasis marker as text
        return {
          type: MinidownNodeType.Text,
          value: startToken.value,
        }
      }
    }

    const children: MinidownNode[] = []
    let previousWasSpace = false
    while (pos < eos) {
      const token = tokens[pos]

      // Check if we're done parsing the node.
      if (tokenEquals(startToken, token)) {
        pos++
        if (children.length > 0) {
          if (previousWasSpace) {
            // Whitespace before the closing tag is not allowed, so turn this
            // into a text node instead.
            const fallback: TextNode = {
              type: MinidownNodeType.Text,
              value: startToken.value,
            }
            prependNode(children, fallback)
            appendNode(children, fallback)
            return children
          }

          return {
            type: MinidownNodeType.Emphasis,
            value: startToken.value,
            children,
          }
        }
      }

      previousWasSpace = token.type === TokenType.Space

      // Still parsing content inside the node
      appendNodes(children, parseInline(true))
    }

    // We didn't find a terminating token, so we'll return the token itself
    // as a text node as well as the parsed children
    prependNode(children, {
      type: MinidownNodeType.Text,
      value: startToken.value,
    })

    return children
  }

  /**
   * Parses text.
   */
  function parseText(linkify: boolean): MinidownNode | MinidownNode[] {
    const token = tokens[pos++]

    if (!linkify) {
      return {
        type: MinidownNodeType.Text,
        value: token.value,
      }
    }

    // Linkify
    const splat = splitByUrls(token.value)
    return splat.map(
      (t): MinidownNode =>
        t.type === 'Text'
          ? { type: MinidownNodeType.Text, value: t.text }
          : {
              type: MinidownNodeType.Link,
              url: t.url,
              children: [{ type: MinidownNodeType.Text, value: t.url }],
            },
    )
  }

  /**
   * Adds nodes to the target array. This mutates {@link target}.
   *
   * @param target The target array to add nodes to.
   * @param node The potential node(s) to add
   * @returns The input target array.
   */
  function appendNodes(
    target: MinidownNode[],
    node: MinidownNode | MinidownNode[],
  ): MinidownNode[] {
    if (Array.isArray(node)) {
      for (const element of node) {
        appendNode(target, element)
      }
    } else {
      appendNode(target, node)
    }
    return target
  }

  /**
   * Appends a node to the target array. This mutates {@link target}.
   * This may mutate already added nodes by compacting them; for example,
   * if {@link target}'s last node is a text node, and we're adding a text node,
   * we will instead concat the text node on the last element.
   *
   * @param target The target array to add nodes to.
   * @param node The potential node(s) to add
   * @returns The input target array.
   */
  function appendNode(target: MinidownNode[], node: MinidownNode) {
    if (target.length > 0 && node.type === MinidownNodeType.Text) {
      const prev = target[target.length - 1]
      if (prev.type === MinidownNodeType.Text) {
        prev.value += node.value
        return target
      }
    }
    target.push(node)
    return target
  }

  /**
   * Appends a text node to the target array. This mutates {@link target}.
   * This may mutate already added nodes by compacting them; for example,
   * if {@link target}'s last node is a text node, and we're adding a text node,
   * we will instead concat the text node on the last element.
   *
   * @param target The target array to add nodes to.
   * @param text The potential node(s) to add
   * @returns The input target array.
   */
  function appendText(target: MinidownNode[], text: string) {
    // NOTE: Currently, this function is only used in contexts where we've
    // already appended something to the target, so this check will always be
    // true, but it doesn't feel like a good idea to not check in case
    // that changes in the future.
    if (target.length > 0) {
      const prev = target[target.length - 1]
      if (prev.type === MinidownNodeType.Text) {
        prev.value += text
        return target
      }
      /* v8 ignore next */
    }

    /* v8 ignore next 7 */
    target.push({
      type: MinidownNodeType.Text,
      value: text,
    })

    return target
  }

  /**
   * Prepends a node to the target array. This mutates {@link target}.
   * This may mutate already added nodes by compacting them; for example,
   * if {@link target}'s first node is a text node, and we're prepending a
   * text node, we will instead concat the value onto the current first element.
   *
   * @param target The target array to prepend nodes to.
   * @param node The potential node(s) to add
   * @returns The input target array.
   */
  function prependNode(target: MinidownNode[], node: MinidownNode) {
    if (target.length > 0 && node.type === MinidownNodeType.Text) {
      const first = target[0]
      if (first.type === MinidownNodeType.Text) {
        first.value = node.value + first.value
        return target
      }
    }
    target.unshift(node)
    return target
  }

  /**
   * Prepends a text node to the target array. This mutates {@link target}.
   * This may mutate already added nodes by compacting them; for example,
   * if {@link target}'s first node is a text node, and we're prepending a
   * text node, we will instead concat the text onto the current first element's
   * text value.
   *
   * @param target The target array to prepend nodes to.
   * @param text The text to prepend.
   * @returns The input target array.
   */
  function prependText(target: MinidownNode[], text: string) {
    if (target.length > 0) {
      const first = target[0]
      if (first.type === MinidownNodeType.Text) {
        first.value = text + first.value
        return target
      }
    }
    target.unshift({
      type: MinidownNodeType.Text,
      value: text,
    })
    return target
  }
}
